import React, { useState, useContext, forwardRef, useRef } from 'react';
import { FaCaretDown } from 'react-icons/fa';
import { useTheme } from 'styled-components';
import { SelectItem } from './SelectItem';
import { Box, BoxProps, ColorVariants, List, ListEntry, OSKThemeType, Overlay } from '..';
import { ErrorContext } from '..';
import CSS from 'csstype';
import { useClickAway } from '../hooks/useClickAway';

export type MultiPickerProps = {
    /** Override the background color of the input box */
    bg?: string;
    /** The default values for this MultiPicker */
    defaultValues?: Array<string | number>;
    /** The error message to display to the user */
    error?: string;
    /** The list of items to display inside this multi-select picker */
    items: Array<ListEntry>;
    /** The name of the form field this element is associated with.
     * If null, there will be no implicit form support. I.E. the
     * <Input type='hidden' /> element won't be rendered. */
    name: string;
    /** The method which is invoked when a change happens. It accepts, as a parameter
     * the current list of selected items.*/
    onChange?: (selected: Array<ListEntry>, oldSelected?: Array<ListEntry>) => void;
    /** The placeholder text or ReactNode to render when no selections are made */
    placeholder?: string | React.ReactNode;
    /** If true, the MultiPicker will be in readonly mode and can't be opened. */
    readonly?: boolean;
    /** The text or ReactNode to render when there are selections (use this
     * to override the default selected text) */
    selectedNode?: string | React.ReactNode;
    /** The color variant to propagate throughout the component. */
    variant: ColorVariants;
    /** Scope style of the input */
    inputStyle?: CSS.Properties;
} & Omit<Omit<BoxProps, 'ref'>, 'onChange'>;

/**
 * A multi-select dropdown box which supports either text entries or react node entries.
 * This component is compatible with forms by default. It will comma-separate the selected
 * values and store them in a hidden input field.
 *
 * Also accepts tab-to-open through a focus trap mechanism.
 */
export const MultiSelect = forwardRef<HTMLInputElement, MultiPickerProps>(
    (
        {
            bg,
            defaultValues,
            error,
            items,
            name,
            onChange,
            placeholder,
            readonly,
            selectedNode,
            variant,
            inputStyle,
            style,
            ...props
        }: MultiPickerProps,
        refOverride,
    ) => {
        const formErrors = useContext(ErrorContext);
        // Calculate the defaultArray if applicable
        const defaultArray: Array<number> = [];
        if (defaultValues) {
            defaultValues.forEach((value) => {
                defaultArray.push(items.findIndex((item) => item.value === value));
            });
        }
        const ref = useRef<HTMLDivElement | null>(refOverride && null);
        const [open, setOpen] = useState(false);
        const [selected, setSelected] = useState<Array<number>>(defaultArray);
        const [_visible, setVisible] = useState(true);
        const theme = useTheme() as OSKThemeType;

        useClickAway(ref, () => setOpen(false), open);

        const getItemIdx = (item: ListEntry) => {
            return items.reduce((prevIdx, subItem, idx) => {
                if (item.value === subItem.value) {
                    return idx;
                } else {
                    return prevIdx;
                }
            }, 0);
        };

        const handleSelect = (item: ListEntry) => {
            const idx = getItemIdx(item);
            const oldSelected = [...selected];
            const nextSelected = [...selected];
            if (nextSelected.includes(idx)) {
                nextSelected.splice(nextSelected.indexOf(idx), 1);
            } else {
                nextSelected.push(idx);
            }

            setSelected(nextSelected);
            setVisible(false);
            setTimeout(setVisible.bind(this, true), 0);
            onChange &&
                onChange(
                    nextSelected.map((selectedIdx) => items[selectedIdx]),
                    oldSelected.map((selectedIdx) => items[selectedIdx]),
                );
        };

        const handleOpen = (shouldOpen: boolean) => {
            if (!readonly) {
                setOpen(shouldOpen);
            }
        };

        const selectedText =
            selected.length === items.length
                ? 'All'
                : selected.length > 0
                ? selectedNode ??
                  selected.map((idx) => (
                      <span key={idx} style={{ paddingRight: 6 }}>
                          {items[idx].label}
                      </span>
                  ))
                : placeholder ?? (readonly === false && <span>Make a Selection</span>) ?? <span></span>;

        const inputBg = theme.colors[variant].bg;
        const inputFg = theme.colors[variant].fg;
        const errorMessage = error ?? formErrors[name];
        return (
            <React.Fragment>
                <Box
                    ref={ref}
                    grow
                    style={{
                        cursor: 'pointer',
                        border: errorMessage
                            ? `2px solid ${errorMessage ? theme.input.errorBorderColor : theme.input.borderColor}`
                            : '',
                        ...style,
                    }}
                    col
                    {...props}
                >
                    {/* This input is a focus-trap. Used to capture tab events.*/}
                    <input
                        style={{
                            position: 'absolute',
                            top: '-1000000000px',
                        }}
                        onBlur={() => handleOpen(false)}
                        onFocus={() => handleOpen(true)}
                    />
                    <Box bg={bg ?? inputBg} fg={inputFg} style={{ ...inputStyle }}>
                        <Box
                            style={{ alignItems: 'center', paddingLeft: '10px', flexWrap: 'wrap' }}
                            onClick={handleOpen.bind(this, !open)}
                            grow
                        >
                            {selectedText}
                        </Box>
                        <Box p={10} onClick={handleOpen.bind(this, !open)}>
                            <FaCaretDown />
                        </Box>
                    </Box>
                    <Overlay
                        animation="fadeIn"
                        style={{ width: '100%', paddingBottom: '50px', paddingTop: '4px' }}
                        show={open}
                    >
                        {open && (
                            <List
                                onSelect={(item) => handleSelect(item)}
                                style={{ width: '100%' }}
                                variant={variant}
                                onCancel={() => handleOpen(false)}
                                items={items.map((item, idx) => {
                                    return {
                                        label:
                                            typeof item.label === 'string' ? (
                                                <SelectItem
                                                    key={item.value}
                                                    text={item.label}
                                                    selected={selected.includes(idx)}
                                                />
                                            ) : (
                                                item.label
                                            ),
                                        value: item.value,
                                    };
                                })}
                            />
                        )}
                    </Overlay>
                    {name && selected.length > 0 && (
                        <input
                            type="hidden"
                            name={name}
                            value={selected.map((_, selectedIdx) => items[selectedIdx].value).join(',')}
                        />
                    )}
                </Box>
                {errorMessage ? `*${errorMessage}` : ''}
            </React.Fragment>
        );
    },
);
